import { Box, Grid, Card, CardContent, Typography, Stack, LinearProgress, Button, Sheet, Tooltip, Chip, Divider, Modal, ModalDialog, Textarea, Select, Option, RadioGroup, Radio, FormControl, FormLabel, Snackbar, CardActions } from "@mui/joy";
import { useContext, useEffect, useState, useRef } from "react";
import axios from "axios";
import { LayoutContext } from "../context";
import { APIGetHomepageData, APIGetDailyQuestion, APIAnswerDailyQuestion, APISubmitReview, APICreateTgPromoInvoice, APIGetFreezeStatus, APIActivateInvoice, API_URL, getAuthConfig } from "../api";
import { useNavigate, Link as RouterLink } from "react-router";
import { Fire, CheckCircle, Exam, TrendUp, CalendarBlank, Plus, Clock, BookOpen, Info, Star, ChatDots, Snowflake, GraduationCap, ArrowRight, Path, ClipboardText, ChartBar, SealCheck, Target } from "@phosphor-icons/react";
import { SafeHTML, formatExplanationHtml } from '../utils';
import FreezeModal from '../components/modals/FreezeModal';
import AccessInfoBanner from '../components/ui/AccessInfoBanner';
import TelegramInviteBanner from '../components/ui/TelegramInviteBanner';


// Countdown timer hook
function useCountdown() {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        const calc = () => {
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            const diff = tomorrow - now;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setTimeLeft(`${h}ч ${m}м ${s}с`);
        };
        calc();
        const interval = setInterval(calc, 1000);
        return () => clearInterval(interval);
    }, []);

    return timeLeft;
}


export default function MainPage() {
    const { token, user, setGenerateTestModalOpen, openUpgradeModal } = useContext(LayoutContext);
    const [homepageData, setHomepageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dailyQ, setDailyQ] = useState(null);
    const [dailyQLoading, setDailyQLoading] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [dailyQResult, setDailyQResult] = useState(null);
    const [showTips, setShowTips] = useState(false);
    // Review modal
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewStep, setReviewStep] = useState('intro'); // 'intro' | 'form'
    const [reviewRating, setReviewRating] = useState(0);
    const [reviewCourse, setReviewCourse] = useState('');
    const [reviewLikedChips, setReviewLikedChips] = useState([]);
    const [reviewLikedText, setReviewLikedText] = useState('');
    const [reviewImproveChips, setReviewImproveChips] = useState([]);
    const [reviewImproveText, setReviewImproveText] = useState('');
    const [reviewRecommend, setReviewRecommend] = useState('');
    const [reviewWishes, setReviewWishes] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewSnackbar, setReviewSnackbar] = useState(false);
    const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);
    const [tgPromoLoading, setTgPromoLoading] = useState(false);
    const [tgPromoPopup, setTgPromoPopup] = useState(false);
    const [freezeStatus, setFreezeStatus] = useState(null);
    const [freezeModalOpen, setFreezeModalOpen] = useState(false);

    const LIKED_OPTIONS = ['Качество вопросов', 'Объяснения к ответам', 'Удобный интерфейс', 'Разнообразие тем', 'Система XP и уровней', 'Модули', 'Вопрос дня', 'Статистика прогресса'];
    const IMPROVE_OPTIONS = ['Больше клинических задач', 'Перевод на русский язык', 'Больше пошаговых курсов', 'Больше объяснений', 'Новые дисциплины', 'Библиотека с видеолекциями', 'Дизайн', 'AI-помощник'];

    const navigate = useNavigate();
    const countdown = useCountdown();

    useEffect(() => {
        loadHomepageData();
        loadFreezeStatus();

        // Refetch when user returns to tab (e.g. after solving a test)
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                loadHomepageData();
                loadFreezeStatus();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);

    const loadFreezeStatus = async () => {
        try {
            const res = await APIGetFreezeStatus(token);
            if (res?.success) setFreezeStatus(res);
        } catch (e) {
            // не критично — кнопка просто не появится
        }
    };

    // Show tooltips after onboarding
    useEffect(() => {
        if (localStorage.getItem('show_onboarding_tooltips') === 'true') {
            setShowTips(true);
            localStorage.removeItem('show_onboarding_tooltips');
            const timer = setTimeout(() => setShowTips(false), 8000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Auto-open review modal — only after 2+ days since registration
    useEffect(() => {
        if (homepageData && !homepageData.has_review && user?.date_create) {
            const created = new Date(user.date_create);
            const now = new Date();
            const daysSinceCreation = (now - created) / (1000 * 60 * 60 * 24);
            if (daysSinceCreation >= 2) {
                setReviewModalOpen(true);
            }
        }
    }, [homepageData, user]);

    const { user_stats, plan_info, weekly_activity, streak, daily_question, user_xp, level_up_pending, level_up_new_level, has_review, tg_promo, pending_activation, subscription, recommendations } = homepageData || {};

    // ── Command center helpers ──
    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 6) return 'Доброй ночи';
        if (h < 12) return 'Доброе утро';
        if (h < 18) return 'Добрый день';
        return 'Добрый вечер';
    })();
    const ccAccuracy = user_stats?.total_answers ? Math.round((user_stats.correct_answers / user_stats.total_answers) * 100) : 0;
    const NAV_TILES = [
        { label: 'Учебный план', hint: 'Пошагово', to: '/plans', icon: Path },
        { label: 'Тесты', hint: 'QBank', to: '/tests', icon: Exam },
        { label: 'Клин. задачи', hint: 'UWorld', to: '/tasks', icon: ClipboardText },
        { label: 'Библиотека', hint: 'First Aid', to: '/modules', icon: BookOpen },
        { label: 'Статистика', hint: 'Прогресс', to: '/stats', icon: ChartBar },
    ];

    const [activating, setActivating] = useState(false);
    const handleActivate = async () => {
        if (!pending_activation?.uuid_pay || activating) return;
        setActivating(true);
        try {
            const res = await APIActivateInvoice(token, pending_activation.uuid_pay);
            if (res?.success) {
                await loadHomepageData();   // перезагрузить — карточка исчезнет, тариф активен
            } else {
                alert(res?.error || 'Не удалось активировать');
            }
        } catch (e) {
            alert('Ошибка активации');
        }
        setActivating(false);
    };

    // TG Promo популярится теперь через карточку «Подписчик закрытого класса»
    // в plan-info блоке (CTA → openUpgradeModal). Старый авто-popup отключён,
    // т.к. он показывал только QBank-тариф без опции APEX PRO.

    const getLevelName = (level) => {
        if (level <= 5) return 'Novice';
        if (level <= 10) return 'Student';
        if (level <= 20) return 'Resident';
        if (level <= 35) return 'Attending';
        return 'Professor';
    };

    const getTierColor = (level) => {
        if (level <= 5) return '#6B7280';
        if (level <= 10) return '#3B82F6';
        if (level <= 20) return '#8B5CF6';
        if (level <= 35) return '#F59E0B';
        return '#EAB308';
    };

    // Load full daily question with answers — always call API (it creates DQ if none exists)
    useEffect(() => {
        if (homepageData) {
            loadFullDailyQuestion();
        }
    }, [homepageData]);

    const loadHomepageData = async () => {
        try {
            const response = await APIGetHomepageData(token);
            if (response.success) {
                setHomepageData(response.data);
                if (response.data.trial_days_remaining !== undefined) {
                    setTrialDaysRemaining(response.data.trial_days_remaining);
                }
            }
        } catch (error) {
            console.error('Error loading homepage data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTgPromo = async (period = 1) => {
        setTgPromoLoading(true);
        try {
            const res = await APICreateTgPromoInvoice(token, period);
            if (res.payment_url) window.location.href = res.payment_url;
        } catch (e) {
            const msg = e?.response?.data?.error || e.message || 'Неизвестная ошибка';
            alert('Ошибка: ' + msg);
        } finally {
            setTgPromoLoading(false);
        }
    };

    const pluralDays = (n) => {
        if (n % 10 === 1 && n % 100 !== 11) return 'день';
        if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'дня';
        return 'дней';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Typography>Загрузка...</Typography>
            </Box>
        );
    }

    const loadFullDailyQuestion = async () => {
        setDailyQLoading(true);
        try {
            const res = await APIGetDailyQuestion(token);
            if (res.success) {
                setDailyQ({
                    id: res.daily_question_id,
                    question_text: res.question?.text || daily_question?.question_text,
                    question_text_ru: res.question?.text_ru || '',
                    clinical_case_text: res.clinical_case_text || daily_question?.clinical_case_text || null,
                    clinical_case_text_ru: res.clinical_case_text_ru || '',
                    answers: res.question?.answers || [],
                    user_answered: res.user_answered,
                    total_answers: res.community_stats?.total_answers || 0,
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDailyQLoading(false);
        }
    };

    const handleDailyAnswer = async (answerId) => {
        setSelectedAnswer(answerId);
        try {
            const res = await APIAnswerDailyQuestion(token, { selected_answer_ids: [answerId] });
            if (res.success) {
                const answers = dailyQ?.answers || [];
                const totalResp = res.community_stats?.total_answers || 1;
                const letters = ['A', 'B', 'C', 'D', 'E'];
                const distribution = answers.map((a, idx) => ({
                    id: a.id,
                    letter: letters[idx] || '',
                    percentage: Math.round(((res.answer_distribution?.[a.id] || 0) / totalResp) * 100),
                    is_correct: a.id === res.correct_answer_id,
                }));
                setDailyQResult({
                    is_correct: res.is_correct,
                    total_answers: totalResp,
                    answer_distribution: distribution,
                    explanation: res.explanation,
                    explanation_ru: res.explanation_ru,
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    // Language helper for RU/EN toggle (shared with TestSessionPage via localStorage)
    const lang = (() => { try { return localStorage.getItem('testLang') || 'en'; } catch { return 'en'; } })();
    const t = (en, ru) => lang === 'ru' && ru ? ru : en;

    const toggleChip = (chip, selected, setSelected) => {
        setSelected(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);
    };

    const handleSubmitReview = async () => {
        const likedCombined = [...reviewLikedChips, reviewLikedText.trim()].filter(Boolean).join('; ');
        const improveCombined = [...reviewImproveChips, reviewImproveText.trim()].filter(Boolean).join('; ');
        if (!reviewRating || !reviewCourse || !likedCombined || !improveCombined || !reviewRecommend || !reviewWishes.trim()) return;
        setReviewSubmitting(true);
        try {
            const res = await APISubmitReview(token, {
                rating: reviewRating,
                course: reviewCourse,
                liked_most: likedCombined,
                improvement: improveCombined,
                would_recommend: reviewRecommend,
                wishes: reviewWishes.trim(),
            });
            if (res.success) {
                setReviewModalOpen(false);
                setReviewSnackbar(true);
                // Update homepage data to hide banner
                setHomepageData(prev => ({ ...prev, has_review: true, review_bonus_active: true }));
            }
        } catch (e) {
            console.error('Review submit error:', e);
        } finally {
            setReviewSubmitting(false);
        }
    };

    const accuracy = user_stats?.total_answers > 0
        ? Math.round((user_stats.correct_answers / user_stats.total_answers) * 100)
        : 0;

    const todayDate = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
        } catch { return iso; }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
            {/* Header */}
            <Typography level="h2" sx={{ mb: 3 }}>
                Главная
            </Typography>

            {/* Карточка активации — тариф оплачен, но не активирован */}
            {/* ═══════════════ COMMAND CENTER ═══════════════ */}
            <Box sx={{ mb: { xs: 3, md: 4.5 } }}>

                {/* Greeting */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end"
                    sx={{ mb: { xs: 2, md: 2.75 }, flexWrap: 'wrap', rowGap: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#B84C31', mb: 0.75 }}>
                            {greeting}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.95, color: 'text.primary' }}>
                            {user?.first_name || 'Студент'}
                        </Typography>
                    </Box>
                    {(streak?.current || 0) > 0 && (
                        <Stack direction="row" alignItems="center" spacing={0.75}
                            sx={{ px: 1.5, py: 0.75, borderRadius: 999, bgcolor: 'rgba(218,119,86,0.10)', border: '1px solid rgba(218,119,86,0.22)' }}>
                            <Fire size={16} weight="fill" color="#DA7756" />
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#B84C31' }}>
                                {streak.current} {pluralDays(streak.current)} подряд
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                {/* Инфо для платных: где что находится (лекции/тесты/поддержка) */}
                {user?.has_active_subscription && (
                    <AccessInfoBanner sx={{ mb: 1.75 }} />
                )}

                {/* Годовым — приглашение в закрытый чат. Компонент сам решает,
                    показываться ли, и помнит, что его закрыли. */}
                <TelegramInviteBanner subscription={subscription} sx={{ mb: 1.75 }} />

                {/* Status + Next step */}
                <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
                    <Grid xs={12} md={7}>
                        {(() => {
                            const wrap = (children) => (
                                <Sheet sx={{ height: '100%', p: { xs: 2.25, md: 2.75 }, borderRadius: 18,
                                    bgcolor: 'background.surface', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 10px rgba(55,53,47,0.04)' }}>
                                    {children}
                                </Sheet>
                            );
                            const label = (t, color) => (
                                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color, mb: 1 }}>{t}</Typography>
                            );
                            if (subscription) return wrap(<>
                                {label('Твой тариф', 'text.secondary')}
                                <Typography sx={{ fontSize: { xs: 22, md: 25 }, fontWeight: 800, letterSpacing: '-0.025em', color: 'text.primary', lineHeight: 1.1 }}>
                                    {subscription.name}{subscription.period ? ` · ${subscription.period}` : ''}
                                </Typography>
                                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.75 }}>
                                    Активен до <Box component="b" sx={{ color: 'text.primary' }}>{subscription.expires_at}</Box>
                                    {subscription.days_remaining != null ? ` · осталось ${subscription.days_remaining} ${pluralDays(subscription.days_remaining)}` : ''}
                                </Typography>
                                {subscription.days_remaining != null && subscription.days_remaining <= 14 && (
                                    <Button size="sm" onClick={() => openUpgradeModal('Продли доступ, чтобы не потерять прогресс.')}
                                        sx={{ mt: 1.5, bgcolor: '#0F7B6C', color: '#fff', fontWeight: 600, borderRadius: 8, '&:hover': { bgcolor: '#0C655A' } }}>Продлить</Button>
                                )}
                            </>, '#0F7B6C');
                            if (pending_activation && !pending_activation.expired) return wrap(<>
                                {label('✓ Тариф оплачен', '#0F7B6C')}
                                <Typography sx={{ fontSize: { xs: 19, md: 21 }, fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1.15 }}>
                                    {({ qbank_only: 'QBank — тесты + клин. задачи', pro: 'APEX PRO — полный доступ', pro_promo: 'APEX PRO — полный доступ' }[pending_activation.product_code]) || 'Тариф'}
                                    {pending_activation.period ? ` · ${pending_activation.period}` : ''}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75, lineHeight: 1.5 }}>
                                    Срок начнётся с момента активации. Активировать можно в течение года{pending_activation.activate_until ? ` — до ${pending_activation.activate_until}` : ''}.
                                </Typography>
                                <Button onClick={handleActivate} loading={activating}
                                    sx={{ mt: 1.5, bgcolor: '#0F7B6C', color: '#fff', fontWeight: 600, borderRadius: 8, px: 2.5, '&:hover': { bgcolor: '#0C655A' } }}>Активировать тариф</Button>
                            </>, '#0F7B6C');
                            if (pending_activation && pending_activation.expired) return wrap(<>
                                {label('Активация истекла', '#B84C31')}
                                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Срок активации (12 мес) прошёл. <a href="https://t.me/ashurovmed" target="_blank" rel="noopener noreferrer" style={{ color: '#B84C31', fontWeight: 600 }}>Напишите в поддержку</a>, чтобы восстановить доступ.</Typography>
                            </>, '#B84C31');
                            if ((trialDaysRemaining || 0) > 0) return wrap(<>
                                {label('Пробный доступ', '#B84C31')}
                                <Typography sx={{ fontSize: { xs: 22, md: 25 }, fontWeight: 800, letterSpacing: '-0.025em', color: 'text.primary', lineHeight: 1.1 }}>
                                    Осталось {trialDaysRemaining} {pluralDays(trialDaysRemaining)}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75 }}>Открыты Биохимия и Иммунология. Оформи полный курс — 6000+ тестов, видео, планы.</Typography>
                                <Button size="sm" onClick={() => openUpgradeModal('Открой весь курс APEX.')}
                                    sx={{ mt: 1.5, bgcolor: '#B84C31', color: '#fff', fontWeight: 600, borderRadius: 8, '&:hover': { bgcolor: '#A03E26' } }}>Открыть полный доступ</Button>
                            </>, '#DA7756');
                            return wrap(<>
                                {label('Бесплатный доступ', 'text.secondary')}
                                <Typography sx={{ fontSize: { xs: 20, md: 23 }, fontWeight: 800, letterSpacing: '-0.025em', color: 'text.primary', lineHeight: 1.1 }}>Открой весь курс APEX</Typography>
                                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75 }}>6000+ тестов, 480+ клин. задач, видео-лекции, учебные планы.</Typography>
                                <Button size="sm" onClick={() => openUpgradeModal('Оформи полный доступ.')}
                                    sx={{ mt: 1.5, bgcolor: '#37352F', color: '#fff', fontWeight: 600, borderRadius: 8, '&:hover': { bgcolor: '#1F1F1F' } }}>Выбрать тариф</Button>
                            </>, '#37352F');
                        })()}
                    </Grid>

                    <Grid xs={12} md={5}>
                        {(() => {
                            const rec = recommendations?.next_action;
                            const onGo = () => { if (rec?.category_id) navigate(`/test?category=${rec.category_id}`); else navigate('/plans'); };
                            return (
                                <Sheet onClick={onGo} sx={{ cursor: 'pointer', height: '100%', p: { xs: 2.25, md: 2.75 }, borderRadius: 18,
                                    bgcolor: '#37352F', color: '#fff', border: '1px solid #37352F', display: 'flex', flexDirection: 'column',
                                    transition: 'transform .18s ease, box-shadow .18s ease', boxShadow: '0 2px 10px rgba(55,53,47,0.10)',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 26px rgba(55,53,47,0.20)' } }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <Target size={15} weight="fill" color="#DA7756" />
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>Что дальше</Typography>
                                    </Stack>
                                    <Typography sx={{ fontSize: { xs: 15.5, md: 16.5 }, fontWeight: 700, lineHeight: 1.3, color: '#fff', flex: 1 }}>
                                        {rec?.text || 'Продолжи учебный план — иди по пошаговой системе APEX.'}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#DA7756' }}>{rec?.category_id ? 'Начать' : 'К плану'}</Typography>
                                        <ArrowRight size={16} weight="bold" color="#DA7756" />
                                    </Stack>
                                </Sheet>
                            );
                        })()}
                    </Grid>
                </Grid>

                {/* Progress strip */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.75, mb: 1.75 }}>
                    {[
                        { n: user_stats?.total_answers ?? 0, l: 'вопросов решено', icon: CheckCircle, c: '#0F7B6C' },
                        { n: `${ccAccuracy}%`, l: 'точность', icon: Target, c: '#B84C31' },
                        { n: user_stats?.completed_tests ?? 0, l: 'тестов пройдено', icon: Exam, c: '#37352F' },
                        { n: streak?.current ?? 0, l: 'дней подряд', icon: Fire, c: '#DA7756' },
                    ].map((s, i) => (
                        <Sheet key={i} sx={{ p: { xs: 1.75, md: 2 }, borderRadius: 16, bgcolor: 'background.surface', border: '1px solid', borderColor: 'divider' }}>
                            <s.icon size={18} weight="fill" color={s.c} />
                            <Typography sx={{ fontSize: { xs: 24, md: 27 }, fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary', mt: 0.75, lineHeight: 1 }}>{s.n}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{s.l}</Typography>
                        </Sheet>
                    ))}
                </Box>

                {/* Nav tiles */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(5,1fr)' }, gap: 1.75 }}>
                    {NAV_TILES.map((t) => (
                        <Sheet key={t.to} onClick={() => navigate(t.to)} sx={{ cursor: 'pointer', p: { xs: 1.75, md: 2 }, borderRadius: 16,
                            bgcolor: 'background.surface', border: '1px solid', borderColor: 'divider',
                            transition: 'transform .16s ease, border-color .16s ease, box-shadow .16s ease',
                            '&:hover': { transform: 'translateY(-2px)', borderColor: 'rgba(184,76,49,0.4)', boxShadow: '0 6px 18px rgba(55,53,47,0.10)' } }}>
                            <Box sx={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(184,76,49,0.10)', mb: 1.25 }}>
                                <t.icon size={18} weight="fill" color="#B84C31" />
                            </Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>{t.label}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>{t.hint}</Typography>
                        </Sheet>
                    ))}
                </Box>
            </Box>

            {/* Frozen banner — показывается, когда подписка сейчас заморожена */}
            {freezeStatus?.is_frozen && (
                <Sheet
                    variant="soft"
                    color="primary"
                    sx={{
                        mb: 3, p: 2, borderRadius: 'lg',
                        display: 'flex', alignItems: 'center', gap: 2,
                        bgcolor: 'rgba(45, 122, 179, 0.10)',
                        border: '1px solid rgba(45, 122, 179, 0.25)',
                    }}
                >
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '50%',
                        bgcolor: 'rgba(45, 122, 179, 0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Snowflake size={22} weight="bold" color="#2D7AB3" />
                    </Box>
                    <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                            Подписка заморожена до {formatDate(freezeStatus.freeze_end)}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            На время заморозки доступ к тестам и модулям закрыт. Срок подписки автоматически продлён.
                        </Typography>
                    </Box>
                </Sheet>
            )}

            {/* Freeze Modal */}
            <FreezeModal
                open={freezeModalOpen}
                onClose={() => setFreezeModalOpen(false)}
                token={token}
                expiresAt={freezeStatus?.expires_at}
                onFrozen={() => loadFreezeStatus()}
            />

            {/* TG Promo Popup Modal */}
            {/* TG Promo Popup Modal */}
            <Modal open={tgPromoPopup} onClose={() => setTgPromoPopup(false)}>
                <ModalDialog sx={{ maxWidth: 560, borderRadius: 'xl', p: 0, overflow: 'hidden' }}>
                    <Box sx={{
                        p: 3, pb: 2,
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                        color: '#fff', textAlign: 'center',
                    }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 1.5, background: 'linear-gradient(135deg, #E8511A, #FF7043)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Star size={24} color="#fff" weight="fill" />
                        </Box>
                        <Typography level="h4" sx={{ color: '#fff', mb: 0.5 }}>Про подписка для учеников</Typography>
                        <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.7)' }}>5 700+ тестов, безлимит модулей, учебные планы на RUS и ENG</Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                            {[
                                { months: 1, price: '1 590', perMonth: '1 590', badge: null },
                                { months: 3, price: '3 990', perMonth: '1 330', badge: null },
                                { months: 6, price: '6 990', perMonth: '1 165', badge: 'Выгоднее' },
                            ].map((plan) => (
                                <Grid xs={4} key={plan.months}>
                                    <Sheet
                                        variant="outlined"
                                        sx={{
                                            p: 2, borderRadius: 'lg', textAlign: 'center', cursor: 'pointer',
                                            border: plan.months === 6 ? '2px solid #DA7756' : undefined,
                                            position: 'relative',
                                            '&:hover': { borderColor: '#DA7756' },
                                        }}
                                        onClick={() => { setTgPromoPopup(false); handleTgPromo(plan.months); }}
                                    >
                                        {plan.badge && (
                                            <Chip size="sm" sx={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', bgcolor: '#DA7756', color: '#fff', fontSize: '0.65rem' }}>{plan.badge}</Chip>
                                        )}
                                        <Typography level="title-sm" sx={{ mb: 0.5 }}>{plan.months} мес</Typography>
                                        <Typography level="h4" sx={{ color: '#DA7756', mb: 0.5 }}>{plan.price} &#8381;</Typography>
                                        {plan.months > 1 && (
                                            <Typography level="body-xs" color="neutral">{plan.perMonth} &#8381;/мес</Typography>
                                        )}
                                    </Sheet>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography level="body-xs" sx={{ textAlign: 'center', color: 'neutral.500', mb: 1 }}>
                            Акция действует 7 дней. Цена закрепляется, пока активна подписка в TG.
                        </Typography>
                        <Button fullWidth variant="plain" color="neutral" size="sm" onClick={() => setTgPromoPopup(false)}>Позже</Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Trial Welcome Banner — фокус на Иммунологии.
                Показывается только active_trial юзерам, не админам, не платным
                (вкл. оплаченных-но-не-активированных → !pending_activation). */}
            {trialDaysRemaining !== null && trialDaysRemaining > 0 && user?.role !== 'admin' && !pending_activation && (
                <Sheet
                    variant="outlined"
                    sx={{
                        mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 'xl',
                        bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(218, 119, 86, 0.08)' : 'rgba(218, 119, 86, 0.04)',
                        border: '1.5px solid',
                        borderColor: '#DA7756',
                    }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                                color: '#B84C31',
                                mb: 0.5,
                            }}>
                                ✨ {trialDaysRemaining} {pluralDays(trialDaysRemaining)} пробного доступа · модуль Иммунологии
                            </Typography>
                            <Typography level="title-md" sx={{ fontWeight: 700, mb: 1 }}>
                                Иммунология открыта полностью
                            </Typography>
                            <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle size={14} color="#0F7B6C" weight="fill" />
                                    <Typography level="body-sm">Все мини-тесты (200+ вопросов)</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle size={14} color="#0F7B6C" weight="fill" />
                                    <Typography level="body-sm">Все клинические задачи (200+ кейсов)</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle size={14} color="#0F7B6C" weight="fill" />
                                    <Typography level="body-sm">Все модули First Aid + учебный план (10 дней)</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle size={14} color="#0F7B6C" weight="fill" />
                                    <Typography level="body-sm">6 видео-лекций по Иммунологии</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircle size={14} color="#0F7B6C" weight="fill" />
                                    <Typography level="body-sm">Русские переводы</Typography>
                                </Stack>
                            </Stack>
                        </Box>
                        <Stack spacing={1} sx={{ minWidth: { md: 220 } }}>
                            <Button
                                variant="solid"
                                onClick={() => setGenerateTestModalOpen(true)}
                                sx={{ bgcolor: '#DA7756', '&:hover': { bgcolor: '#C4623E' }, borderRadius: 'lg' }}
                            >
                                Создать тест
                            </Button>
                            <Button
                                component={RouterLink}
                                to="/plans"
                                variant="outlined"
                                color="neutral"
                                sx={{ borderRadius: 'lg' }}
                            >
                                Открыть план Иммунологии
                            </Button>
                        </Stack>
                    </Stack>
                </Sheet>
            )}

            {/* Section 1: Mini Stats Row */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <Grid xs={6} md={3}>
                    <Tooltip title="Ваша серия дней подряд" open={showTips} placement="bottom" arrow>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', textAlign: 'center' }}>
                        <Box sx={{ color: streak?.is_active ? '#DA7756' : 'neutral.400', mb: 0.5, display: 'flex', justifyContent: 'center' }}><Fire size={24} weight="fill" /></Box>
                        <Typography level="h3" sx={{ color: streak?.is_active ? '#DA7756' : 'neutral.500' }}>
                            {streak?.current || 0}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            {(streak?.current || 0) === 1 ? 'день подряд' : (streak?.current || 0) >= 2 && (streak?.current || 0) <= 4 ? 'дня подряд' : 'дней подряд'}
                        </Typography>
                    </Sheet>
                    </Tooltip>
                </Grid>
                <Grid xs={6} md={3}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', textAlign: 'center' }}>
                        <Box sx={{ color: streak?.goal_reached ? 'success.500' : 'neutral.400', mb: 0.5, display: 'flex', justifyContent: 'center' }}><CheckCircle size={24} weight="fill" /></Box>
                        <Typography level="h3">
                            {streak?.today_progress || 0}/{streak?.daily_goal || 20}
                        </Typography>
                        <Typography level="body-xs" color="neutral">вопросов сегодня</Typography>
                        <LinearProgress
                            value={Math.min(100, ((streak?.today_progress || 0) / (streak?.daily_goal || 20)) * 100)}
                            determinate
                            color={streak?.goal_reached ? 'success' : 'primary'}
                            sx={{ mt: 1, height: 4, borderRadius: 2 }}
                        />
                    </Sheet>
                </Grid>
                <Grid xs={6} md={3}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', textAlign: 'center' }}>
                        <Box sx={{ color: 'neutral.400', mb: 0.5, display: 'flex', justifyContent: 'center' }}><Exam size={24} /></Box>
                        <Typography level="h3">
                            {user_stats?.completed_tests || 0}
                        </Typography>
                        <Typography level="body-xs" color="neutral">тестов завершено</Typography>
                    </Sheet>
                </Grid>
                <Grid xs={6} md={3}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', textAlign: 'center' }}>
                        <Box sx={{ color: accuracy >= 70 ? 'success.500' : accuracy >= 40 ? 'warning.500' : 'neutral.400', mb: 0.5, display: 'flex', justifyContent: 'center' }}><TrendUp size={24} /></Box>
                        <Typography level="h3">
                            {accuracy}%
                        </Typography>
                        <Typography level="body-xs" color="neutral">средний % правильных</Typography>
                    </Sheet>
                </Grid>
            </Grid>

            {/* Row: XP card + Plan/TG card side-by-side (each md=6) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid xs={12} md={6}>
            {user_xp && (() => {
                const tierColor = getTierColor(user_xp.level);
                const tierGradients = {
                    '#6B7280': 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #1f2937 100%)',
                    '#3B82F6': 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #1e3a5f 100%)',
                    '#8B5CF6': 'linear-gradient(135deg, #2e1065 0%, #5b21b6 50%, #2e1065 100%)',
                    '#F59E0B': 'linear-gradient(135deg, #451a03 0%, #92400e 50%, #451a03 100%)',
                    '#EAB308': 'linear-gradient(135deg, #422006 0%, #854d0e 50%, #422006 100%)',
                };
                const barGradients = {
                    '#6B7280': 'linear-gradient(90deg, #6B7280, #9CA3AF)',
                    '#3B82F6': 'linear-gradient(90deg, #3B82F6, #60A5FA)',
                    '#8B5CF6': 'linear-gradient(90deg, #8B5CF6, #A78BFA)',
                    '#F59E0B': 'linear-gradient(90deg, #F59E0B, #FBBF24)',
                    '#EAB308': 'linear-gradient(90deg, #EAB308, #FDE047)',
                };
                return (
                    <Card sx={{
                        borderRadius: 'xl', overflow: 'hidden',
                        height: '100%',
                        background: tierGradients[tierColor],
                        border: 'none',
                        boxShadow: `0 4px 24px ${tierColor}22`,
                    }}>
                        <CardContent sx={{ py: 2.5, px: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.5, py: 0.5,
                                    borderRadius: '20px',
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(8px)',
                                    border: `1px solid rgba(255,255,255,0.2)`,
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.05em',
                                }}>
                                    LVL {user_xp.level} · {getLevelName(user_xp.level)}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', opacity: 0.9 }}>
                                        {user_xp.total.toLocaleString()} XP
                                    </Typography>
                                    <Tooltip
                                        variant="outlined"
                                        placement="bottom-end"
                                        title={
                                            <Box sx={{ p: 1, maxWidth: 240 }}>
                                                <Typography level="title-sm" sx={{ mb: 1, fontWeight: 700 }}>Как начисляется XP?</Typography>
                                                <Typography level="body-xs" sx={{ mb: 0.5 }}>✅ Правильный ответ — <b>+10 XP</b></Typography>
                                                <Typography level="body-xs" sx={{ mb: 0.5 }}>❌ Неправильный ответ — <b>+2 XP</b></Typography>
                                                <Typography level="body-xs" sx={{ mb: 0.5 }}>🏥 Клинический кейс — <b>+20 XP</b></Typography>
                                                <Typography level="body-xs" sx={{ mb: 0.5 }}>📅 Вопрос дня — <b>+15 XP</b></Typography>
                                                <Typography level="body-xs">💯 Тест на 100% — <b>+25 XP</b> бонус</Typography>
                                            </Box>
                                        }
                                    >
                                        <Box sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.9)' }, display: 'flex' }}><Info size={16} /></Box>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box sx={{
                                position: 'relative', height: 10, borderRadius: 5,
                                bgcolor: 'rgba(255,255,255,0.12)',
                                overflow: 'hidden',
                            }}>
                                <Box sx={{
                                    position: 'absolute', left: 0, top: 0, bottom: 0,
                                    width: `${user_xp.progress_percentage}%`,
                                    background: barGradients[tierColor],
                                    borderRadius: 5,
                                    transition: 'width 0.6s ease',
                                    boxShadow: `0 0 12px ${tierColor}88`,
                                }} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                                    {user_xp.xp_in_level.toLocaleString()} / {user_xp.xp_for_next_level.toLocaleString()} XP
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                                    до Level {user_xp.level + 1}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })()}
            </Grid>

            {plan_info && (
                <Grid xs={12} md={6}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 'xl',
                            height: '100%',
                            ...(tg_promo?.eligible && {
                                borderColor: '#B84C31',
                                borderWidth: '1.5px',
                                bgcolor: 'rgba(184, 76, 49, 0.03)',
                            }),
                        }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            {/* TG promo eyebrow */}
                            {tg_promo?.eligible && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '50%',
                                        bgcolor: 'rgba(184, 76, 49, 0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <GraduationCap size={13} color="#B84C31" weight="fill" />
                                    </Box>
                                    <Typography sx={{
                                        color: '#B84C31',
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                    }}>
                                        Подписчик закрытого класса
                                    </Typography>
                                </Stack>
                            )}

                            {/* Tariff + counter row */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography level="body-sm">
                                    Тариф: <b>{trialDaysRemaining !== null ? `Триал (${trialDaysRemaining} ${pluralDays(trialDaysRemaining)})` : plan_info?.current_plan?.name}</b>
                                </Typography>
                                {plan_info?.current_plan?.plan_type === 'free' && plan_info?.daily_limit > 0 ? (
                                    <Typography level="body-xs" color="neutral">
                                        {plan_info.questions_today} из {plan_info.daily_limit} вопросов сегодня
                                    </Typography>
                                ) : (
                                    <Typography level="body-xs" color="neutral">
                                        Сегодня: {plan_info.questions_today} вопросов
                                    </Typography>
                                )}
                            </Stack>
                            {plan_info?.current_plan?.plan_type === 'free' && plan_info?.daily_limit > 0 && (
                                <>
                                    <LinearProgress
                                        value={plan_info.progress_percentage}
                                        determinate
                                        color={plan_info.remaining_questions > 0 ? 'primary' : 'danger'}
                                        sx={{ height: 4, borderRadius: 2 }}
                                    />
                                    {plan_info.remaining_questions <= 0 && (
                                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                            Лимит исчерпан
                                        </Typography>
                                    )}
                                </>
                            )}

                            {/* TG promo: что открыто + CTA — только для free юзеров.
                                Раньше было plan_type !== 'plan1', что показывало плашку
                                всем grand_* платящим юзерам тоже (баг). */}
                            {tg_promo?.eligible && plan_info?.current_plan?.plan_type === 'free' && (
                                <>
                                    <Divider sx={{ my: 1.5, borderColor: 'rgba(184, 76, 49, 0.2)' }} />
                                    <Typography sx={{
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        fontSize: '0.62rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        mb: 0.85,
                                    }}>
                                        Что открыто
                                    </Typography>
                                    <Stack spacing={0.6} sx={{ mb: 1.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <CheckCircle size={14} color="#0F7B6C" weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
                                            <Typography level="body-xs">
                                                <b>Кардиология</b> — без лимитов на вопросы
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <CheckCircle size={14} color="#0F7B6C" weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
                                            <Typography level="body-xs">
                                                <b>Биохимия</b> и <b>Иммунология</b> — модули и до 10 вопросов/день
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <CheckCircle size={14} color="#0F7B6C" weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
                                            <Typography level="body-xs">
                                                Спец-цена на полный курс (от 1 590 ₽)
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        size="sm"
                                        fullWidth
                                        endDecorator={<ArrowRight size={14} weight="bold" />}
                                        onClick={() => openUpgradeModal && openUpgradeModal('Открой полный курс по цене закрытого класса')}
                                        sx={{
                                            borderRadius: 'lg',
                                            bgcolor: '#B84C31',
                                            '&:hover': { bgcolor: '#9C3F28' },
                                            fontWeight: 600,
                                        }}
                                    >
                                        Полный доступ
                                    </Button>
                                </>
                            )}

                            {/* TG promo + paid: благодарность */}
                            {tg_promo?.eligible && plan_info?.current_plan?.plan_type === 'plan1' && (
                                <>
                                    <Divider sx={{ my: 1.5, borderColor: 'rgba(184, 76, 49, 0.2)' }} />
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Спасибо за поддержку. Полный доступ открыт.
                                    </Typography>
                                </>
                            )}

                            {/* Заморозка — доступна только для grand_12m promo */}
                            {freezeStatus?.can_freeze && !freezeStatus?.is_frozen && (
                                <Button
                                    variant="soft"
                                    color="primary"
                                    size="sm"
                                    startDecorator={<Snowflake size={16} weight="bold" />}
                                    onClick={() => setFreezeModalOpen(true)}
                                    sx={{ mt: 1.5, borderRadius: 'lg' }}
                                >
                                    Заморозить подписку
                                </Button>
                            )}
                            {freezeStatus?.is_frozen && (
                                <Typography level="body-xs" sx={{ mt: 1, color: '#2D7AB3', fontWeight: 600 }}>
                                    ❄ Заморожена до {formatDate(freezeStatus.freeze_end)}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
            </Grid>

            {/* Review Banner — show only after 2+ days */}
            {!has_review && user?.date_create && ((new Date() - new Date(user.date_create)) / (1000 * 60 * 60 * 24) >= 2) && (
                <Card sx={{
                    borderRadius: 'xl', mb: 3, overflow: 'hidden',
                    background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(234,88,12,0.3)' },
                }} onClick={() => setReviewModalOpen(true)}>
                    <CardContent sx={{ py: 2.5, px: 2.5 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <ChatDots size={36} color="#fff" style={{ opacity: 0.9 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 0.25 }}>
                                    Оставьте отзыв — получите +20 задач на 5 дней!
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                                    Помогите нам стать лучше и получите бонус
                                </Typography>
                            </Box>
                            <Button
                                size="sm"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={(e) => { e.stopPropagation(); setReviewModalOpen(true); }}
                            >
                                Оставить отзыв
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Personalized Recommendations */}
            {homepageData?.recommendations && (
                <Card variant="outlined" sx={{ borderRadius: 'xl', mb: 3, border: '1.5px solid #DA775630' }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#DA775615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: '14px' }}>🎯</Typography>
                            </Box>
                            <Typography level="title-lg">Рекомендовано для тебя</Typography>
                        </Stack>

                        {/* Next action */}
                        {homepageData.recommendations.next_action && (
                            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg', mb: 2, bgcolor: '#DA775610' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography level="body-md" fontWeight={600}>
                                        {homepageData.recommendations.next_action.text}
                                    </Typography>
                                    <Button
                                        size="sm"
                                        variant="solid"
                                        color="primary"
                                        onClick={() => {
                                            if (homepageData.recommendations.next_action.category_id) {
                                                navigate(`/test?category=${homepageData.recommendations.next_action.category_id}`);
                                            } else {
                                                navigate('/test');
                                            }
                                        }}
                                        sx={{ flexShrink: 0, ml: 2 }}
                                    >
                                        Начать
                                    </Button>
                                </Stack>
                            </Sheet>
                        )}

                        {/* Weak topics progress */}
                        {homepageData.recommendations.weak_topics?.length > 0 && (
                            <Stack spacing={1.5}>
                                {homepageData.recommendations.weak_topics.map((topic, i) => (
                                    <Box key={i}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                            <Typography level="body-sm" fontWeight={500}>
                                                {topic.name}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography level="body-xs" color="neutral">
                                                    {topic.answered}/{topic.total_questions} вопросов
                                                </Typography>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={topic.accuracy >= 70 ? 'success' : topic.accuracy >= 40 ? 'warning' : 'danger'}
                                                    sx={{ minWidth: 48, justifyContent: 'center' }}
                                                >
                                                    {topic.accuracy}%
                                                </Chip>
                                            </Stack>
                                        </Stack>
                                        <LinearProgress
                                            determinate
                                            value={topic.progress}
                                            sx={{
                                                height: 6,
                                                borderRadius: 'sm',
                                                bgcolor: 'neutral.100',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: topic.accuracy >= 70 ? '#0F7B6C' : topic.accuracy >= 40 ? '#F59E0B' : '#E03E3E',
                                                },
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {/* Exam + daily plan info */}
                        {(homepageData.recommendations.exam_info || homepageData.recommendations.daily_plan) && (
                            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                                {homepageData.recommendations.exam_info && (
                                    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', flex: 1, textAlign: 'center' }}>
                                        <Typography level="body-xs" color="neutral">Экзамен</Typography>
                                        <Typography level="body-sm" fontWeight={600}>
                                            {homepageData.recommendations.exam_info.label}
                                        </Typography>
                                    </Sheet>
                                )}
                                {homepageData.recommendations.daily_plan && (
                                    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', flex: 1, textAlign: 'center' }}>
                                        <Typography level="body-xs" color="neutral">Цель на день</Typography>
                                        <Typography level="body-sm" fontWeight={600}>
                                            ~{homepageData.recommendations.daily_plan.recommended_questions} вопросов
                                        </Typography>
                                    </Sheet>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Section 2: Daily Question (full width) */}
            <Tooltip title="Отвечайте каждый день" open={showTips} placement="top" arrow>
            <Card variant="outlined" sx={{ borderRadius: 'xl', mb: 3 }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarBlank size={20} color="#B84C31" />
                            <Typography level="title-lg">Вопрос дня</Typography>
                        </Stack>
                        <Typography level="body-xs" color="neutral">{todayDate}</Typography>
                    </Stack>

                    {dailyQLoading ? (
                        <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
                            <LinearProgress sx={{ width: '60%' }} />
                            <Typography level="body-xs" color="neutral">Загрузка вопроса...</Typography>
                        </Stack>
                    ) : dailyQResult ? (
                        <Stack spacing={1.5}>
                            <Sheet variant="soft" color={dailyQResult.is_correct ? 'success' : 'danger'} sx={{ p: 2, borderRadius: 'md' }}>
                                <Typography level="body-sm" fontWeight="bold">
                                    {dailyQResult.is_correct ? 'Правильно!' : 'Неправильно'}
                                </Typography>
                            </Sheet>
                            {dailyQResult.explanation && (
                                <Sheet variant="soft" color="neutral" sx={{ p: 2, borderRadius: 'md' }}>
                                    <Typography level="body-xs" sx={{ mb: 0.5 }} fontWeight="bold">Объяснение:</Typography>
                                    <Typography level="body-xs">
                                        <SafeHTML html={formatExplanationHtml(t(dailyQResult.explanation, dailyQResult.explanation_ru))} />
                                    </Typography>
                                </Sheet>
                            )}
                            {dailyQResult.answer_distribution && (
                                <Stack spacing={0.5}>
                                    {dailyQResult.answer_distribution.map((a) => (
                                        <Stack key={a.id} direction="row" alignItems="center" spacing={1}>
                                            <Typography level="body-xs" sx={{ minWidth: 20 }}>{a.letter}.</Typography>
                                            <LinearProgress
                                                value={a.percentage}
                                                determinate
                                                color={a.is_correct ? 'success' : 'neutral'}
                                                sx={{ flex: 1 }}
                                            />
                                            <Typography level="body-xs" sx={{ minWidth: 35 }}>{a.percentage}%</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                            <Divider sx={{ my: 0.5 }} />
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ color: 'neutral.400', display: 'flex' }}><Clock size={14} /></Box>
                                <Typography level="body-xs" color="neutral">
                                    Следующий вопрос через {countdown}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : dailyQ?.answers?.length > 0 && !dailyQ?.user_answered ? (
                        <Stack spacing={1.5}>
                            {dailyQ.clinical_case_text && (
                                <Sheet variant="soft" color="neutral" sx={{ p: 2, borderRadius: 'md', bgcolor: 'neutral.50' }}>
                                    <Typography level="body-xs" fontWeight={700} sx={{ mb: 0.5, color: 'neutral.600', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                                        Клинический случай
                                    </Typography>
                                    <Typography level="body-md">
                                        <SafeHTML html={t(dailyQ.clinical_case_text, dailyQ.clinical_case_text_ru)} />
                                    </Typography>
                                </Sheet>
                            )}
                            <Typography level="body-sm" fontWeight={600}>
                                <SafeHTML html={t(dailyQ.question_text, dailyQ.question_text_ru)} />
                            </Typography>
                            <Stack spacing={1}>
                                {dailyQ.answers.map((a, idx) => (
                                    <Button
                                        key={a.id}
                                        variant={selectedAnswer === a.id ? 'solid' : 'outlined'}
                                        color="neutral"
                                        size="sm"
                                        onClick={() => handleDailyAnswer(a.id)}
                                        disabled={selectedAnswer !== null}
                                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                    >
                                        {String.fromCharCode(65 + idx)}. <SafeHTML html={t(a.text, a.text_ru)} />
                                    </Button>
                                ))}
                            </Stack>
                            {dailyQ.total_answers > 0 && (
                                <Typography level="body-xs" color="neutral">
                                    Уже ответили: {dailyQ.total_answers} чел.
                                </Typography>
                            )}
                        </Stack>
                    ) : dailyQ?.user_answered ? (
                        <Stack spacing={1.5}>
                            <Typography level="body-sm" color="neutral">
                                Вы уже ответили на вопрос дня. Приходите завтра!
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ color: 'neutral.400', display: 'flex' }}><Clock size={14} /></Box>
                                <Typography level="body-xs" color="neutral">
                                    Следующий вопрос через {countdown}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography level="body-sm" color="neutral">
                            Вопрос дня скоро появится!
                        </Typography>
                    )}
                </CardContent>
            </Card>
            </Tooltip>

            {/* Section 3: Bottom row */}
            <Grid container spacing={2}>
                {/* Weekly Activity */}
                <Grid xs={12} md={7}>
                    <Card variant="outlined" sx={{ borderRadius: 'xl', height: '100%' }}>
                        <CardContent>
                            <Typography level="title-lg" sx={{ mb: 2 }}>Активность за неделю</Typography>
                            {weekly_activity && (
                                <Stack spacing={1}>
                                    {weekly_activity.map((day) => (
                                        <Stack key={day.date} direction="row" alignItems="center" spacing={1.5}>
                                            <Typography level="body-xs" sx={{ minWidth: 24, color: 'neutral.500' }}>
                                                {day.day_name}
                                            </Typography>
                                            <LinearProgress
                                                value={Math.min(100, (day.total_activity / (day.max_possible || 1)) * 100)}
                                                determinate
                                                sx={{
                                                    flexGrow: 1, height: 6, borderRadius: 3,
                                                    bgcolor: 'rgba(76, 175, 80, 0.12)',
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: day.total_activity > 0 ? '#4CAF50' : 'transparent',
                                                    },
                                                }}
                                            />
                                            <Typography level="body-xs" sx={{ minWidth: 24, textAlign: 'right', color: 'neutral.500' }}>
                                                {day.total_activity}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Plan + Quick Actions */}
                <Grid xs={12} md={5}>
                    <Stack spacing={2} sx={{ height: '100%' }}>
                        {/* Plan/TG card moved up to share row with XP card */}

                        {/* Quick actions */}
                        <Tooltip title="Начните с создания теста" open={showTips} placement="left" arrow>
                        <Button
                            variant="solid"
                            color="primary"
                            size="lg"
                            startDecorator={<Plus size={18} />}
                            onClick={() => setGenerateTestModalOpen(true)}
                            sx={{ borderRadius: 'xl', py: 2 }}
                        >
                            Создать тест
                        </Button>
                        </Tooltip>

                        <Button
                            component={RouterLink}
                            to="/modules"
                            replace
                            variant="outlined"
                            color="neutral"
                            startDecorator={<BookOpen size={18} />}
                            sx={{ borderRadius: 'xl' }}
                        >
                            Модули
                        </Button>

                        <Button
                            component={RouterLink}
                            to="/stats"
                            replace
                            variant="outlined"
                            color="neutral"
                            sx={{ borderRadius: 'xl' }}
                        >
                            Подробная статистика
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Review Modal */}
            <Modal open={reviewModalOpen} onClose={() => {
                setReviewModalOpen(false);
                setReviewStep('intro');
            }}>
                <ModalDialog sx={{ maxWidth: 500, borderRadius: 'xl', p: 3, overflow: 'auto', maxHeight: '90vh' }}>
                    {reviewStep === 'intro' ? (
                        /* ===== Intro step ===== */
                        <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: '50%',
                                bgcolor: '#37352F',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <ChatDots size={32} color="#fff" />
                            </Box>
                            <Typography level="h4">Помогите нам стать лучше!</Typography>
                            <Typography level="body-sm" color="neutral" sx={{ maxWidth: 360 }}>
                                Мы хотим сделать APEX QBank максимально полезным для вашей подготовки. Ваш отзыв поможет нам понять, что работает хорошо и что стоит улучшить.
                            </Typography>
                            <Sheet variant="soft" color="success" sx={{ p: 2, borderRadius: 'lg', width: '100%' }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                                    <Typography level="title-sm" color="success">🎁 Бонус за отзыв:</Typography>
                                    <Typography level="body-sm" fontWeight={600}>+20 задач в день на 5 дней</Typography>
                                </Stack>
                            </Sheet>
                            <Stack spacing={1} sx={{ width: '100%', textAlign: 'left' }}>
                                <Typography level="body-xs" color="neutral">Опрос займёт ~1 минуту. Нужно:</Typography>
                                <Stack spacing={0.5} sx={{ pl: 1 }}>
                                    <Typography level="body-xs" color="neutral">⭐ Поставить оценку</Typography>
                                    <Typography level="body-xs" color="neutral">🎓 Указать курс</Typography>
                                    <Typography level="body-xs" color="neutral">👍 Выбрать что нравится</Typography>
                                    <Typography level="body-xs" color="neutral">💡 Предложить улучшения</Typography>
                                    <Typography level="body-xs" color="neutral">📢 Рекомендация друзьям</Typography>
                                    <Typography level="body-xs" color="neutral">💬 Любые пожелания</Typography>
                                </Stack>
                            </Stack>
                            <Button
                                fullWidth size="lg"
                                onClick={() => setReviewStep('form')}
                                sx={{
                                    bgcolor: '#37352F',
                                    '&:hover': { bgcolor: '#4a4a45' },
                                }}
                            >
                                Начать опрос
                            </Button>
                        </Stack>
                    ) : (
                        /* ===== Form step ===== */
                        <>
                            <Typography level="h4" sx={{ mb: 0.5 }}>Оставьте отзыв</Typography>
                            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>5 вопросов · ~1 минута</Typography>

                            {/* Rating */}
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Оценка</FormLabel>
                                <Stack direction="row" spacing={0.5}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Box
                                            key={star}
                                            onClick={() => setReviewRating(star)}
                                            sx={{ cursor: 'pointer', transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.2)' } }}
                                        >
                                            {star <= reviewRating
                                                ? <Star size={32} color="#F59E0B" weight="fill" />
                                                : <Box sx={{ color: 'neutral.400', display: 'flex' }}><Star size={32} /></Box>
                                            }
                                        </Box>
                                    ))}
                                </Stack>
                            </FormControl>

                            {/* Course */}
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Курс</FormLabel>
                                <Select
                                    placeholder="Выберите курс"
                                    value={reviewCourse}
                                    onChange={(_, val) => setReviewCourse(val)}
                                >
                                    <Option value="1">1 курс</Option>
                                    <Option value="2">2 курс</Option>
                                    <Option value="3">3 курс</Option>
                                    <Option value="4">4 курс</Option>
                                    <Option value="5">5 курс</Option>
                                    <Option value="6">6 курс</Option>
                                    <Option value="ординатура">Ординатура</Option>
                                    <Option value="врач">Врач</Option>
                                </Select>
                            </FormControl>

                            {/* Liked most — chips + text */}
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Что вам нравится?</FormLabel>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                                    {LIKED_OPTIONS.map((opt) => (
                                        <Chip
                                            key={opt}
                                            size="sm"
                                            variant={reviewLikedChips.includes(opt) ? 'solid' : 'outlined'}
                                            color={reviewLikedChips.includes(opt) ? 'primary' : 'neutral'}
                                            onClick={() => toggleChip(opt, reviewLikedChips, setReviewLikedChips)}
                                            sx={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                        >
                                            {opt}
                                        </Chip>
                                    ))}
                                </Stack>
                                <Textarea
                                    minRows={1}
                                    placeholder="Или напишите своё..."
                                    value={reviewLikedText}
                                    onChange={(e) => setReviewLikedText(e.target.value)}
                                    sx={{ fontSize: '0.85rem' }}
                                />
                            </FormControl>

                            {/* Improvement — chips + text */}
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Что можно улучшить?</FormLabel>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                                    {IMPROVE_OPTIONS.map((opt) => (
                                        <Chip
                                            key={opt}
                                            size="sm"
                                            variant={reviewImproveChips.includes(opt) ? 'solid' : 'outlined'}
                                            color={reviewImproveChips.includes(opt) ? 'warning' : 'neutral'}
                                            onClick={() => toggleChip(opt, reviewImproveChips, setReviewImproveChips)}
                                            sx={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                        >
                                            {opt}
                                        </Chip>
                                    ))}
                                </Stack>
                                <Textarea
                                    minRows={1}
                                    placeholder="Или напишите своё..."
                                    value={reviewImproveText}
                                    onChange={(e) => setReviewImproveText(e.target.value)}
                                    sx={{ fontSize: '0.85rem' }}
                                />
                            </FormControl>

                            {/* Recommendation */}
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Порекомендуете друзьям?</FormLabel>
                                <RadioGroup
                                    value={reviewRecommend}
                                    onChange={(e) => setReviewRecommend(e.target.value)}
                                >
                                    <Stack direction="row" spacing={2}>
                                        <Radio value="yes" label="Да" />
                                        <Radio value="maybe" label="Скорее да" />
                                        <Radio value="no" label="Нет" />
                                    </Stack>
                                </RadioGroup>
                            </FormControl>

                            {/* Wishes — free text, required */}
                            <FormControl sx={{ mb: 3 }}>
                                <FormLabel>Ваши пожелания</FormLabel>
                                <Textarea
                                    minRows={2}
                                    placeholder="Напишите любые пожелания, идеи или комментарии..."
                                    value={reviewWishes}
                                    onChange={(e) => setReviewWishes(e.target.value)}
                                />
                            </FormControl>

                            <Button
                                fullWidth
                                size="lg"
                                loading={reviewSubmitting}
                                disabled={!reviewRating || !reviewCourse || (!reviewLikedChips.length && !reviewLikedText.trim()) || (!reviewImproveChips.length && !reviewImproveText.trim()) || !reviewRecommend || !reviewWishes.trim()}
                                onClick={handleSubmitReview}
                                sx={{
                                    bgcolor: '#37352F',
                                    '&:hover': { bgcolor: '#4a4a45' },
                                }}
                            >
                                Отправить отзыв
                            </Button>
                        </>
                    )}
                </ModalDialog>
            </Modal>

            {/* Review Success Snackbar */}
            <Snackbar
                open={reviewSnackbar}
                autoHideDuration={5000}
                onClose={() => setReviewSnackbar(false)}
                color="success"
                variant="soft"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                Спасибо за отзыв! +20 задач на 5 дней активированы
            </Snackbar>

        </Box>
    );
}
